(() => {
  if (customElements.get('haro-conference-gallery')) return;
  customElements.define('haro-conference-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slides = [...this.querySelectorAll('[data-event-slide]')];
      this.toggle = this.querySelector('[data-event-toggle]');
      this.counter = this.querySelector('[data-event-count]');
      this.desktop = matchMedia('(min-width: 750px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.dataset.ready = "true";
      this.paused = this.motion.matches;
      this.visible = false;
      this.querySelector('[data-event-prev]').addEventListener('click', () => this.move(-1), options);
      this.querySelector('[data-event-next]').addEventListener('click', () => this.move(1), options);
      this.toggle.addEventListener('click', () => {
        this.paused = !this.paused;
        this.sync();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, options);
      this.desktop.addEventListener('change', () => this.show(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.sync();
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.querySelectorAll('video').forEach(video => {
        video.defaultMuted = true;
        video.muted = true;
        const audioButton = video.parentElement.querySelector('[data-event-audio]');
        if (audioButton) {
          audioButton.hidden = false;
          audioButton.addEventListener('click', () => { video.muted = !video.muted; }, options);
          video.addEventListener('volumechange', () => this.updateAudioButton(video), options);
          this.updateAudioButton(video);
        }
        video.addEventListener('ended', () => this.schedule(), options);
        video.addEventListener('error', () => this.schedule(), options);
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.querySelector('[data-event-controls]').hidden = false;
      this.show();
    }
    updateAudioButton(video) {
      const button = video.parentElement.querySelector('[data-event-audio]');
      if (!button) return;
      const label = video.muted ? button.dataset.unmuteLabel : button.dataset.muteLabel;
      button.dataset.muted = String(video.muted);
      button.setAttribute('aria-label', label);
      button.title = label;
    }
    get count() { return this.desktop.matches ? 2 : 1; }
    get running() { return this.visible && !document.hidden && !this.paused; }
    load(slide, preloadOnly = false) {
      const img = slide.querySelector('img');
      if (img && img.dataset.src) {
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        delete img.dataset.src;
      }
      const postcard = slide.querySelector('[data-postcard-src]');
      if (postcard) {
        const width = slide.getBoundingClientRect().width * (window.devicePixelRatio || 1);
        postcard.setAttribute('href', width <= 640 ? postcard.dataset.postcardSmall : postcard.dataset.postcardSrc);
        delete postcard.dataset.postcardSrc;
      }
      const video = slide.querySelector('video');
      if (video && !preloadOnly) {
        if (video.dataset.poster) {
          video.poster = video.dataset.poster;
          delete video.dataset.poster;
        }
        if (this.running && video.dataset.src) {
          video.src = video.dataset.src;
          delete video.dataset.src;
          video.load();
        }
      }
    }
    move(direction, manual = true) {
      const pages = Math.ceil(this.slides.length / this.count);
      this.index = ((Math.floor(this.index / this.count) + direction + pages) % pages) * this.count;
      this.show(manual);
    }
    show(manual = false) {
      this.index = Math.floor(this.index / this.count) * this.count;
      this.active = this.slides.slice(this.index, this.index + this.count);
      this.slides.forEach(slide => {
        slide.hidden = !this.active.includes(slide);
        const video = slide.querySelector('video');
        if (slide.hidden && video) {
          video.pause();
          video.muted = true;
          this.updateAudioButton(video);
          if (video.readyState) video.currentTime = 0;
        }
      });
      this.counter.setAttribute('aria-live', manual ? 'polite' : 'off');
      const positions = this.active.map(slide => this.slides.indexOf(slide) + 1);
      this.counter.textContent = `${positions.join('–')} / ${this.slides.length}`;
      this.sync();
    }
    sync() {
      clearTimeout(this.timer);
      this.toggle.dataset.paused = String(this.paused);
      this.toggle.setAttribute('aria-label', this.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.active.forEach(slide => {
        if (this.visible) this.load(slide);
        const video = slide.querySelector('video');
        if (!video) return;
        if (this.running && video.src && !video.ended) {
          video.play().catch(() => {
            if (this.running && this.active.includes(slide)) {
              this.paused = true;
              this.sync();
            }
          });
        } else video.pause();
      });
      if (this.visible) {
        for (let n = 0; n < this.count; n++) {
          this.load(this.slides[(this.index + this.count + n) % this.slides.length], true);
        }
      }
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.running) return;
      const videos = this.active.map(slide => slide.querySelector('video')).filter(Boolean);
      if (videos.some(video => !video.ended && !video.error)) return;
      this.timer = setTimeout(() => this.move(1, false), videos.length ? 1200 : 6500);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.querySelectorAll('video').forEach(video => video.pause());
      this.controller = null;
    }
  });
})();
